/**
 * Textos da home (proposta do Google Stitch). Os textos serão revisados pela Medviz;
 * altere aqui sem mexer nos templates.
 */

export interface NavLink {
  label: string;
  section: string;
}

export interface PainPoint {
  icon: string;
  title: string;
  description: string;
  solution: string;
  seal: string;
}

export interface ModuleFeature {
  eyebrow: string;
  title: string;
  description: string;
  items: string[];
}

export interface GalleryItem {
  label: string;
  mockup: 'dashboard' | 'documents' | 'education';
}

export interface Differentiator {
  icon: string;
  title: string;
  description: string;
}

export interface ExternalLink {
  label: string;
  href: string;
  icon: string;
}

export interface Resource {
  icon: string;
  title: string;
  description: string;
  href: string;
  /** Idioma do site de destino, quando não for português. */
  language?: string;
  links: ExternalLink[];
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FooterColumn {
  title: string;
  links: { label: string; section?: string; route?: string }[];
}

export const CONTACT_EMAIL = 'medviz@medviz.com.br';

export const NAV_LINKS: NavLink[] = [
  { label: 'Soluções', section: 'solucoes' },
  { label: 'Evidências', section: 'evidencias' },
  { label: 'Módulos', section: 'modulos' },
  { label: 'Segurança', section: 'seguranca' },
  { label: 'Materiais', section: 'materiais' }
];

export const HERO = {
  badges: ['Acreditação Hospitalar', 'Conformidade LGPD'],
  title: 'Evidências rastreáveis para auditorias clínicas.',
  description:
    'Centralize documentação, credenciamento e avaliações com trilha de ações auditável — construído para padrões ONA e JCI.',
  highlights: [
    'Painel único de conformidade e pendências',
    'Alertas de vencimento e ciclos de avaliação',
    'Relatórios por unidade e especialidade'
  ],
  primaryCta: 'Agendar demonstração guiada',
  secondaryCta: 'Ver tour do produto'
};

export const PAIN_POINTS = {
  title: 'Prevenção de não-conformidades técnicas e administrativas.',
  items: [
    {
      icon: 'assignment_late',
      title: 'Documentos vencidos',
      description:
        'Evite que CRMs, alvarás e especializações expirem sem aviso prévio. A gestão centralizada garante visibilidade total.',
      solution: 'Alertas automáticos com até 90 dias de antecedência.',
      seal: 'Verified Control'
    },
    {
      icon: 'history_edu',
      title: 'Falta de evidência',
      description:
        'Sem rastreabilidade, o esforço da equipe clínica é invisível para a acreditação. O Medviz gera o Audit Trail necessário.',
      solution: 'Trilha de auditoria completa para cada ação no sistema.',
      seal: 'Audit Ready'
    },
    {
      icon: 'verified',
      title: 'Avaliações irregulares',
      description:
        'Ciclos de avaliação fora do prazo comprometem a governança. Padronize formulários e fluxos de trabalho.',
      solution: 'Fluxos com prazos rígidos para OPME e pares.',
      seal: 'Clinical Excellence'
    }
  ] satisfies PainPoint[]
};

export const MODULES: ModuleFeature[] = [
  {
    eyebrow: 'Módulo 01',
    title: 'Dossiê eletrônico e credenciamento ágil',
    description:
      'Elimine arquivos físicos. Tenha um repositório centralizado onde cada profissional tem seu histórico validado digitalmente.',
    items: [
      'Upload descentralizado via portal do médico.',
      'Fluxos de validação técnica por coordenação.',
      'Exportação em PDF para auditorias em segundos.'
    ]
  },
  {
    eyebrow: 'Módulo 02',
    title: 'Avaliação de desempenho baseada em evidências',
    description:
      'Transforme avaliações subjetivas em dados técnicos auditáveis, conforme exigido por normas internacionais.',
    items: [
      'Formulários customizáveis para OPME e satisfação.',
      'Agendamento cíclico com notificações automáticas.',
      'Dashboard de evolução por especialidade clínica.'
    ]
  },
  {
    eyebrow: 'Módulo 03',
    title: 'Escopo de atuação e privilégios clínicos',
    description:
      'Garanta segurança ao paciente certificando que cada procedimento é realizado por profissional tecnicamente validado.',
    items: [
      'Controle refinado por procedimento ou CID.',
      'Workflow baseado em títulos e proficiência.',
      'Compliance jurídico institucional assegurado.'
    ]
  }
];

export const GALLERY = {
  title: 'Arquitetura de Dados em HealthTech.',
  description: 'Interfaces projetadas para alta performance e precisão técnica.',
  cta: 'Ver módulos',
  items: [
    { label: 'Dashboard Executivo', mockup: 'dashboard' },
    { label: 'Gestão Documental', mockup: 'documents' },
    { label: 'Educação Continuada', mockup: 'education' }
  ] satisfies GalleryItem[]
};

export const DIFFERENTIATORS = {
  title: 'Governança Integrada de Alta Performance.',
  items: [
    { icon: 'smartphone', title: 'Mobile-first', description: 'Captura ágil de documentos.' },
    { icon: 'account_tree', title: 'Audit Trail', description: 'Rastreabilidade ponta a ponta.' },
    { icon: 'domain', title: 'Multi-unidade', description: 'Gestão de grupos hospitalares.' },
    { icon: 'draw', title: 'Assinatura', description: 'Validade jurídica garantida.' },
    { icon: 'gavel', title: 'Segurança', description: 'Blindagem de dados LGPD.' }
  ] satisfies Differentiator[]
};

export const RESOURCES = {
  title: 'Recursos para Auditoria.',
  description:
    'Acesse as referências oficiais das principais acreditações hospitalares e prepare sua instituição com base nos requisitos de cada metodologia.',
  items: [
    {
      icon: 'verified',
      title: 'Acreditação ONA',
      description:
        'Metodologia brasileira da Organização Nacional de Acreditação: conheça a jornada, os níveis de certificação e os requisitos para sua instituição.',
      href: 'https://ona.org.br/jornadas/acreditacao/sobre',
      links: [
        { label: 'Sobre a acreditação', href: 'https://ona.org.br/jornadas/acreditacao/sobre', icon: 'info' },
        { label: 'Biblioteca', href: 'https://ona.org.br/biblioteca', icon: 'menu_book' }
      ]
    },
    {
      icon: 'public',
      title: 'Acreditação JCI',
      description:
        'Padrão internacional da Joint Commission International para qualidade e segurança do paciente: entenda o processo de acreditação.',
      href: 'https://www.jointcommission.org/en/accreditation',
      language: 'Conteúdo em inglês',
      links: [
        { label: 'Sobre a acreditação', href: 'https://www.jointcommission.org/en/accreditation', icon: 'info' },
        { label: 'Biblioteca', href: 'https://www.jointcommission.org/en/knowledge-library', icon: 'menu_book' }
      ]
    }
  ] satisfies Resource[]
};

export const FAQ = {
  title: 'Perguntas Frequentes',
  items: [
    {
      question: 'Tempo de implantação do sistema?',
      answer: 'Setup completo em média de 30 dias, incluindo importação e treinamento.'
    },
    {
      question: 'Suporte a acreditações internacionais?',
      answer: 'Sim, desenhado especificamente para atender padrões JCI e Qmentum.'
    }
  ] satisfies FaqItem[]
};

export const DEMO_REQUEST = {
  title: 'Excelência clínica com rastreabilidade total.',
  description:
    'Agende uma sessão técnica para analisar como o Medviz estrutura a governança do seu corpo clínico.',
  sealTitle: 'Verified Reliability',
  sealText:
    '"Dados protegidos e demonstrações 100% baseadas em perfis fictícios de alta fidelidade para garantir o sigilo institucional."',
  submit: 'Solicitar Demonstração'
};

export const FOOTER = {
  tagline:
    'Precision in Clinical Governance and Compliance Evidence. Built for the future of healthcare accreditation.',
  columns: [
    {
      title: 'Soluções',
      links: [
        { label: 'Credenciamento', section: 'evidencias' },
        { label: 'Privilégios', section: 'evidencias' },
        { label: 'Avaliação de Desempenho', section: 'evidencias' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Política de privacidade', route: '/privacy-policy' },
        { label: 'Termos de uso', route: '/terms-use' },
        { label: 'Aviso legal', route: '/legal-notice' }
      ]
    },
    {
      title: 'Suporte',
      links: [
        { label: 'Knowledge Base', section: 'faq' },
        { label: 'API Documentation', section: 'contato' }
      ]
    }
  ] as FooterColumn[],
  copyright: 'Medviz. All rights reserved. Precision in Compliance.',
  signature: 'Certified clinical systems'
};
